import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import type TranscriptTurnFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/TranscriptReviewSection/FormData/TranscriptTurnFormData';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type TranscriptSpeakerView from '@UI/Store/Interviews/Views/TranscriptSpeakerView';
import type TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';
import { css } from '@emotion/react';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { Button, Tag, TextArea } from '@verclam/ui-kit';

const rowStyle = (isActive: boolean) => css`
	display: flex;
	gap: 1.25rem;
	padding: 1rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${isActive ? theme.colors.accentSurface : theme.colors.surface};
	border: 1.5px solid ${isActive ? theme.colors.accentDark : theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
		gap: 0.75rem;
	}
`;

const asideStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.375rem;
	width: 10rem;
	flex: none;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		width: auto;
	}
`;

const speakerButtonStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	background: transparent;
	border: none;
	padding: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.ink};
`;

const speakerLabelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.ink};
`;

const timecodeButtonStyle = css`
	background: transparent;
	border: none;
	padding: 0;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.accentDark};
`;

const timecodeTextStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkFaint};
`;

const bodyStyle = css`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const textButtonStyle = css`
	all: unset;
	cursor: pointer;
	display: block;
	width: 100%;
	font-family: ${theme.fonts.body};
	font-size: 1rem;
	line-height: 1.7;
	color: ${theme.colors.ink};
`;

const plainTextStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 1rem;
	line-height: 1.7;
	color: ${theme.colors.ink};
`;

const revisedBadgeStyle = css`
	align-self: flex-start;
`;

const editFooterStyle = css`
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
`;

type Props = {
	readonly turn: TranscriptTurnView;
	readonly speaker: TranscriptSpeakerView | null;
	readonly hasSpeakers: boolean;
	readonly isActive: boolean;
	readonly canSeek: boolean;
	readonly onSeek: (turn: TranscriptTurnView) => void;
	readonly isEditing: boolean;
	readonly formData: TranscriptTurnFormData;
	readonly isSubmitting: boolean;
	readonly onChangeText: (value: string) => void;
	readonly onSubmit: () => void;
	readonly onCancel: () => void;
	/** Already wrapped by the anti-loss guard (`runGuarded`) — this row never decides that itself. */
	readonly onRequestEdit: (turn: TranscriptTurnView) => void;
	readonly onRequestAssignSpeaker: (turn: TranscriptTurnView) => void;
	/** `false` renders the speaker label and the text read-only, whatever the permissions. */
	readonly isWritable: boolean;
};

/**
 * One card per turn (§9.4). `hasSpeakers` decides whether the left column renders a speaker button at
 * all — without diarization there is nothing to attribute, so no button is shown, matching V-24's
 * "aucune diarisation" state. `canSeek` alone decides whether the timecode is a button or plain text:
 * it already folds both seek conditions (`turn.hasTimestamp` and the media being positionable), so
 * this component never re-derives them.
 */
const TranscriptTurnRow: React.FC<Props> = ({
	turn,
	speaker,
	hasSpeakers,
	isActive,
	canSeek,
	onSeek,
	isEditing,
	formData,
	isSubmitting,
	onChangeText,
	onSubmit,
	onCancel,
	onRequestEdit,
	onRequestAssignSpeaker,
	isWritable,
}) => {
	const textAreaId = React.useId();

	const handleSeekClick = React.useCallback(() => onSeek(turn), [onSeek, turn]);
	const handleEditClick = React.useCallback(() => onRequestEdit(turn), [onRequestEdit, turn]);
	const handleAssignClick = React.useCallback(
		() => onRequestAssignSpeaker(turn),
		[onRequestAssignSpeaker, turn],
	);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				onSubmit();
				return;
			}

			if (event.key === 'Escape') {
				event.preventDefault();
				onCancel();
			}
		},
		[onSubmit, onCancel],
	);

	const speakerLabel = speaker?.name ?? 'Locuteur inconnu';

	return (
		<div css={rowStyle(isActive)}>
			<div css={asideStyle}>
				{hasSpeakers && !isWritable ? <span css={speakerLabelStyle}>{speakerLabel}</span> : null}
				{hasSpeakers && isWritable ? (
					<WithPermission
						permission={PermissionEnum.INTERVIEW_WRITE}
						fallback={<span css={speakerLabelStyle}>{speakerLabel}</span>}
					>
						<button type="button" css={speakerButtonStyle} onClick={handleAssignClick}>
							{speakerLabel}
							<Pencil size={11} aria-hidden="true" />
						</button>
					</WithPermission>
				) : null}
				{canSeek ? (
					<button type="button" css={timecodeButtonStyle} onClick={handleSeekClick}>
						{turn.timecodeLabel}
					</button>
				) : turn.timecodeLabel !== null ? (
					<span css={timecodeTextStyle}>{turn.timecodeLabel}</span>
				) : null}
				{hasSpeakers && speaker !== null ? (
					<Tag value={speaker.provenanceLabel} severity="secondary" />
				) : null}
			</div>
			<div css={bodyStyle}>
				{turn.isRevised ? (
					<span css={revisedBadgeStyle}>
						<Tag value="Modifié" severity="info" title={turn.originalText ?? undefined} />
					</span>
				) : null}
				{isEditing && isWritable ? (
					<React.Fragment>
						<TextArea
							name={`turn-${turn.id}-text`}
							id={textAreaId}
							label="Texte du tour"
							value={formData.text}
							onChange={onChangeText}
							onKeyDown={handleKeyDown}
							errorMessage={formData.getValidationMessage('text')}
							isFormFieldValid={formData.isValid('text')}
							autoResize
							autoFocus
							rows={3}
						/>
						<div css={editFooterStyle}>
							<Button
								type="button"
								label="Annuler"
								severity="secondary"
								outlined
								onClick={onCancel}
							/>
							<Button
								type="button"
								label="Enregistrer"
								loading={isSubmitting}
								onClick={onSubmit}
							/>
						</div>
					</React.Fragment>
				) : !isWritable ? (
					<p css={plainTextStyle}>{turn.text}</p>
				) : (
					<WithPermission
						permission={PermissionEnum.INTERVIEW_WRITE}
						fallback={<p css={plainTextStyle}>{turn.text}</p>}
					>
						<button type="button" css={textButtonStyle} onClick={handleEditClick}>
							{turn.text}
						</button>
					</WithPermission>
				)}
			</div>
		</div>
	);
};

export default TranscriptTurnRow;
